import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { absHourOf, dayToDate, formatDate, formatDateTime } from '@pk/shared';
import type { ChronicleEntry } from '@pk/shared';
import { useChronicle, useWorld } from '../api/hooks';
import { Btn, Collapsible, Empty, ErrorBox, Loading, PageHeader, Screen, ScreenTop, ScrollArea, Segmented } from '../components/ui';
import { IconBook } from '../components/icons';
import '../styles/screens-people.css';

type Scope = 'mine' | 'all';

export function ChronicleScreen() {
  const { t } = useTranslation();
  const [scope, setScope] = useState<Scope>('mine');
  const q = useChronicle(scope === 'mine');
  const hall = useWorld().data?.hallOfFame ?? [];
  const entries = useMemo(() => q.data?.pages.flat() ?? [], [q.data]);
  const groups = useMemo(() => {
    const out: { year: number; items: ChronicleEntry[] }[] = [];
    for (const e of entries) {
      const year = dayToDate(e.day).year;
      const last = out[out.length - 1];
      if (last && last.year === year) last.items.push(e);
      else out.push({ year, items: [e] });
    }
    return out;
  }, [entries]);

  return (
    <Screen>
      <ScreenTop>
      <PageHeader icon={<IconBook />} title={t('chronicle.title')} subtitle={t('chronicle.sub')} />
      <div className="tl-controls">
        <Segmented<Scope> value={scope} onChange={setScope} options={[
          { value: 'mine', label: t('chronicle.mine') }, { value: 'all', label: t('chronicle.all') },
        ]} />
        <span className="tl-legend"><span aria-hidden="true" style={{ color: 'var(--gold-2)' }}>&#9670;</span> {t('chronicle.legend')}</span>
      </div>
      </ScreenTop>

      <ScrollArea>
      {hall.length > 0 && (
        <Collapsible title={t('renown.hallOfFame')} summary={hall.length} className="flush">
          <ul className="list hall-of-fame">
            {hall.map((e) => (
              <li key={e.id}><div className="grow"><b>{e.name}</b><span className="lr-sub">{t('renown.hallEntry', { renown: e.renown, date: formatDate(e.deathDay) })}</span></div></li>
            ))}
          </ul>
        </Collapsible>
      )}
      {q.isLoading && <Loading />}
      {q.isError && <ErrorBox error={q.error} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && entries.length === 0 && <Empty>{t('chronicle.empty')}</Empty>}

      {groups.map((g) => (
        <section key={g.year} className="tl-year" aria-label={t('chronicle.year', { year: g.year })}>
          <div className="tl-year-head">
            <h2>{t('chronicle.year', { year: g.year })}</h2>
            <span className="rule" aria-hidden="true" />
            <span className="count">{t('chronicle.entries', { count: g.items.length })}</span>
          </div>
          <ol className="tl">
            {g.items.map((e) => (
              <li key={e.id} className={`tl-item ${e.important ? 'important' : ''}`}>
                <div className="tl-meta">
                  <span className="tl-when">{e.hour != null ? formatDateTime(absHourOf(e.day, e.hour)) : formatDate(e.day)}</span>
                  <span className="tl-kind">{e.important && <span aria-hidden="true">&#9670; </span>}{t(`chronicle.kind.${e.kind}`)}</span>
                </div>
                <p className="tl-text">{e.text}</p>
              </li>
            ))}
          </ol>
        </section>
      ))}

      {q.hasNextPage && (
        <div className="tl-more">
          <Btn block variant="quiet" busy={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>{t('chronicle.more')}</Btn>
        </div>
      )}
      {!q.hasNextPage && entries.length > 0 && <p className="center muted small" style={{ margin: '16px 0' }}>{t('chronicle.end')}</p>}
      </ScrollArea>
    </Screen>
  );
}
